import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Workspace } from '../types';
import { apiRequest } from '../utils/api';
import { useAuth } from './AuthContext';

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  setCurrentWorkspace: (workspace: Workspace) => void;
  loading: boolean;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [workspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, token } = useAuth();

  // Load workspace from localStorage on mount and listen for changes
  useEffect(() => {
    const loadWorkspace = async () => {
      try {
        // If not authenticated, don't try to load workspace
        if (!isAuthenticated || !token) {
          setLoading(false);
          return;
        }

        // Validate workspace against token first
        let tokenWorkspaceId: string | null = null;
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          tokenWorkspaceId = tokenData.workspaceId || null;
          
          // If token doesn't have workspaceId and user is not portal admin, clear workspace
          if (!tokenWorkspaceId && !tokenData.isPortalAdmin) {
            localStorage.removeItem('currentWorkspaceId');
            localStorage.removeItem('currentWorkspace');
            setCurrentWorkspaceState(null);
            setLoading(false);
            return;
          }
        } catch (e) {
          // Token parsing failed, clear workspace
          localStorage.removeItem('currentWorkspaceId');
          localStorage.removeItem('currentWorkspace');
          setCurrentWorkspaceState(null);
          setLoading(false);
          return;
        }

        const storedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        const storedWorkspace = localStorage.getItem('currentWorkspace');
        
        // Validate stored workspace matches token
        if (storedWorkspaceId && tokenWorkspaceId && storedWorkspaceId !== tokenWorkspaceId) {
          // Mismatch - clear stored workspace and use token's workspace
          console.warn('Workspace mismatch: clearing stored workspace');
          localStorage.removeItem('currentWorkspaceId');
          localStorage.removeItem('currentWorkspace');
        }
        
        if (storedWorkspace && storedWorkspaceId === tokenWorkspaceId) {
          // Workspace exists and matches token
          try {
            const workspace = JSON.parse(storedWorkspace);
            setCurrentWorkspaceState(workspace);
            setLoading(false);
            return;
          } catch (e) {
            // Parse error, clear and fetch fresh
            localStorage.removeItem('currentWorkspace');
          }
        }
        
        // Need to fetch workspace from API
        if (tokenWorkspaceId) {
          try {
            let workspace: Workspace | null = null;
            try {
              // Try portal endpoint first (works for portal admins)
              workspace = await apiRequest<Workspace>(`/api/portal/workspaces/${tokenWorkspaceId}`, {}, { useAuth: true, bypassWorkspace: true });
            } catch (portalError) {
              // If portal endpoint fails, try to get from /api/auth/me which includes user workspaces
              try {
                const userData = await apiRequest<any>('/api/auth/me', {}, { useAuth: true });
                if (userData.workspaces && Array.isArray(userData.workspaces)) {
                  workspace = userData.workspaces.find((w: Workspace) => w.id === tokenWorkspaceId) || null;
                }
              } catch (authError) {
                console.error('Error fetching workspace from auth endpoint:', authError);
              }
            }
            
            if (workspace) {
              setCurrentWorkspaceState(workspace);
              localStorage.setItem('currentWorkspaceId', workspace.id);
              localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
            } else {
              // Workspace not found, clear it
              localStorage.removeItem('currentWorkspaceId');
              localStorage.removeItem('currentWorkspace');
            }
          } catch (error) {
            console.error('Error fetching workspace:', error);
            // Clear invalid workspace ID
            localStorage.removeItem('currentWorkspaceId');
            localStorage.removeItem('currentWorkspace');
          }
        } else {
          // No workspace in token (portal admin or no workspace assigned)
          localStorage.removeItem('currentWorkspaceId');
          localStorage.removeItem('currentWorkspace');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading workspace:', error);
        setLoading(false);
      }
    };

    loadWorkspace();

    // Listen for storage changes (when workspace is updated in another tab/component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentWorkspace' && e.newValue) {
        try {
          const workspace = JSON.parse(e.newValue);
          setCurrentWorkspaceState(workspace);
        } catch (error) {
          console.error('Error parsing workspace from storage:', error);
        }
      } else if (e.key === 'currentWorkspaceId' && !e.newValue) {
        // Workspace was cleared
        setCurrentWorkspaceState(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically for changes (for same-tab updates)
    const interval = setInterval(() => {
      const storedWorkspace = localStorage.getItem('currentWorkspace');
      const storedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      
      if (storedWorkspace) {
        try {
          const workspace = JSON.parse(storedWorkspace);
          // Only update if workspace actually changed
          setCurrentWorkspaceState(prev => {
            if (!prev || prev.id !== workspace.id) {
              return workspace;
            }
            return prev;
          });
        } catch (error) {
          // Ignore parse errors
        }
      } else if (!storedWorkspaceId) {
        // Workspace was cleared
        setCurrentWorkspaceState(prev => prev ? null : prev);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isAuthenticated, token]);

  const setCurrentWorkspace = (workspace: Workspace) => {
    setCurrentWorkspaceState(workspace);
    localStorage.setItem('currentWorkspaceId', workspace.id);
    localStorage.setItem('currentWorkspace', JSON.stringify(workspace));
  };

  const refreshWorkspaces = async () => {
    // This would fetch user's workspaces
    // For now, we'll handle this in the login flow
  };

  const value: WorkspaceContextType = {
    currentWorkspace,
    workspaces,
    setCurrentWorkspace,
    loading,
    refreshWorkspaces
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

